import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MaskitoDirective } from '@maskito/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { DynamicRouterLinkDirective } from '@personal-area/global/navigation';
import {
  AccountTariff,
  ServerType,
  SettingsForm,
  Tariff,
  defaultCurrency,
  defaultServer,
} from '@personal-area/global/accounts/models';
import { FixRateService } from '@personal-area/global/accounts/services';
import { ErrorTexts } from '@personal-area/global/form-validation/models';
import { Validators } from '@personal-area/global/form-validation/services';
import {
  AlertModule,
  ButtonComponent,
  CardWrapperModule,
  FormFieldComponent,
  Mask,
  TogglerModule,
  UiNotificationComponent,
} from '@personal-area/global/ui';
import { IconSpriteModule } from '@personal-area/icon-sprite';
import { CurrencyLocalizedModule } from '@personal-area/global/i18n';


const DEFAULT_LEVERAGE = 500;

@Component({
    selector: 'app-account-settings',
    templateUrl: './account-settings.component.html',
    styleUrls: ['./account-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    ReactiveFormsModule,
    TranslateModule,
    MatTooltipModule,
    FormFieldComponent,
    MatCheckboxModule,
    ButtonComponent,
    UiNotificationComponent,
    DynamicRouterLinkDirective,
    CardWrapperModule,
    CurrencyLocalizedModule,
    TogglerModule,
    AlertModule,
    IconSpriteModule,
    MaskitoDirective
]
})
export class AccountSettingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tariff: Tariff; // выбранный тариф
  @Input() currency: string;
  @Input() preSelectedServer: ServerType;
  @Input() disableExceptPreSelectedServer = false;
  @Input() isIslamicCountry: boolean;
  @Input() swapFreeDescription: string;
  @Input() isSwapFreeContentVisible: boolean;

  @Output() formSubmitted = new EventEmitter<SettingsForm>();
  @Output() goBack = new EventEmitter<void>();

  public readonly fixRateEnabled = this._fixRateService.fixRateEnabled();
  public readonly maxDemoAmount = 1000000;
  public readonly maskOptions = Mask.getDecimalPlacesMask();
  public readonly submitErrors = new EventEmitter<void>();
  public readonly accountForm = this._formBuilder.nonNullable.group({
    isSwapFreeEnabled: [false],
    tariff: ['', Validators.required],
    currency: ['', Validators.required],
    server: [ServerType.MT5, Validators.required],
    leverage: [null as number | null, Validators.required],
    fixRate: [false],
    amount: [
      20000,
      [Validators.max(this.maxDemoAmount), Validators.pattern(/^[0-9.,]+$/)],
    ],
  });

  private _ngUnsubscribe = new Subject<void>();

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _fixRateService: FixRateService,
    private _location: Location,
  ) {
  }

  public fieldErrors(field: string): ErrorTexts {
    return {
      required: `open-account.error.${field}-required`,
      pattern: 'open-account.error.pattern',
      max: `open-account.error.${field}-max`,
    };
  }

  public ngOnDestroy(): void {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
  }

  public ngOnInit(): void {
    if (this.isSwapFreeContentVisible) {
      this.accountForm.controls.isSwapFreeEnabled
        .patchValue(!this.tariff.isSwapFreeDisableDefault);
    }

    this._updLeverageCtrlState(ServerType.MT5);
    this._patchForm(this.tariff, ServerType.MT5);

    const serverCtrl = this.accountForm.controls.server;
    const currencyCtrl = this.accountForm.controls.currency;

    if (this.fixRateEnabled) {
      currencyCtrl.valueChanges
        .pipe(takeUntil(this._ngUnsubscribe))
        .subscribe((val) => this._updFixRateCtrlState(val));
    }

    serverCtrl.valueChanges
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe((val) => {
        if (this.fixRateEnabled) {
          this._updFixRateCtrlState(currencyCtrl.value);
        }
        this._updLeverageCtrlState(val);
      });

    this._updLeverageCtrlState(serverCtrl.value);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.accountForm && changes['tariff']) {
      this._patchForm(changes['tariff'].currentValue as Tariff);
    }
  }

  public back(): void {
    this._location.back();
    this.goBack.next();
  }

  /**
   * Открытие счета.
   */
  public formSubmit(): void {
    if (this.accountForm.valid) {
      this.formSubmitted.emit(this.accountForm.getRawValue());
    } else {
      this.submitErrors.next();
    }
  }

  public canUseFixRate(t: Tariff): boolean {
    return (
      this.fixRateEnabled &&
      t.currencies.some((c) =>
        this._fixRateService.fixRateCurrencies.includes(c),
      )
    );
  }

  public get fixedRate(): number {
    return this._fixRateService.getCurrencyRateByCountry();
  }

  public get fixRateCurrency(): string {
    return this._fixRateService.getLocalCurrencyByCountry();
  }

  public get leverages(): number[] {
    return this.tariff.leverages;
  }

  public tariffCurrencies(tariff: Tariff): string[] | null {
    return tariff && tariff.currencies
      ? this.accountForm.controls.server.value === ServerType.TP
        ? tariff.currencies.filter(
          (c) =>
            !this._fixRateService.fixRateCurrencies.includes(c) &&
            c.includes('USD'),
        )
        : tariff.currencies.filter(
          (c) => !this._fixRateService.fixRateCurrencies.includes(c),
        )
      : null;
  }

  public isEveryServerEnabled(tariff: Tariff): boolean {
    return tariff.servers.some((server) => server.isEnabled);
  }

  private _patchForm(tariff: Tariff, preSelectedServer?: ServerType) {
    if (!tariff || !tariff.type) {
      return;
    }
    const currencies = this.tariffCurrencies(tariff) || [defaultCurrency];
    const leverage = this._getLeverageValue(preSelectedServer);
    const currency = currencies.find((c) => c === this.currency);
    const serversName = Tariff.getServersName(tariff.servers);

    this.accountForm.patchValue({
      tariff: tariff.type,
      currency: (currency || currencies[0]).toUpperCase(),
      server:
        preSelectedServer && serversName.includes(preSelectedServer)
          ? preSelectedServer
          : serversName.includes(this.preSelectedServer)
            ? this.preSelectedServer
            : serversName.includes(defaultServer)
              ? defaultServer
              : serversName[0] || defaultServer,
      leverage,
    });

    if (
      this.fixRateEnabled &&
      this.accountForm.get('currency')?.value !== 'USD'
    ) {
      this._disableFixRate();
    }
  }

  private _updFixRateCtrlState(currency: string): void {
    currency === 'USD'
      ? this.accountForm.controls.fixRate.enable()
      : this._disableFixRate();
  }

  private _updLeverageCtrlState(server: ServerType): void {
    this.accountForm.controls.leverage.patchValue(
      this._getLeverageValue(server),
    );
  }

  private _getLeverageValue(preSelectedServer?: ServerType): number | null {
    const leverage = this.tariff.leverages.find((l) => l === DEFAULT_LEVERAGE);
    return this.tariff.leverages.length
      ? leverage ||
      Math.min.apply(
        null,
        preSelectedServer === ServerType.TP
          ? this.leverages
          : this.tariff.leverages,
      )
      : null;
  }

  private _disableFixRate() {
    const fixRateCtrl = this.accountForm.controls.fixRate;
    fixRateCtrl.patchValue(false);
    fixRateCtrl.disable();
  }

  public changeSwapFreeStatus(): void {
    this.accountForm.controls.isSwapFreeEnabled.patchValue(
      !this.accountForm.value.isSwapFreeEnabled,
    );
  }

  public get isProfessional(): boolean {
    return this.tariff.type === AccountTariff.professional;
  }
}
